import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

interface CategoryDropdownProps {
  isLight?: boolean;
  isMobile?: boolean;
}

export default function CategoryDropdown({ isLight = false, isMobile = false }: CategoryDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buttonClass = isMobile
    ? 'whitespace-nowrap flex items-center gap-2 rounded-lg bg-background-100 border border-background-200/70 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-all duration-200 cursor-pointer hover:bg-background-200 w-full justify-between'
    : isLight
      ? 'whitespace-nowrap flex items-center gap-2 rounded-full bg-background-50/15 text-background-50 px-5 py-2.5 text-sm font-medium backdrop-blur-sm transition-all duration-200 cursor-pointer hover:bg-background-50/25'
      : 'whitespace-nowrap flex items-center gap-2 rounded-full bg-foreground-900/70 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur-sm transition-all duration-200 cursor-pointer hover:bg-foreground-800/80';

  return (
    <div ref={dropdownRef} className={`relative ${isMobile ? 'w-full' : ''}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClass}
      >
        Shop Category
        <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-sm"></i>
        </span>
      </button>

      {isOpen && (
        <div className={`${isMobile ? 'relative mt-2 w-full' : 'absolute left-1/2 -translate-x-1/2 top-full mt-2 w-72'} rounded-lg bg-background-50 border border-background-200/70 py-2 z-50 max-h-80 overflow-y-auto`}>
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 transition-colors duration-150 cursor-pointer hover:bg-secondary-100 hover:text-foreground-950"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 flex-shrink-0 overflow-hidden">
                {cat.image ? (
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" title={cat.name} />
                ) : (
                  <span className="text-xs font-semibold text-foreground-400">{cat.name.charAt(0)}</span>
                )}
              </span>
              {cat.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}